// useSettlementHistory.js — 정산 설정 이력 타임라인 훅
// 이력 조회, 신규 날짜 추가, 삭제를 관리

import { useState, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';

export function useSettlementHistory() {
  const { showToast } = useToast();

  // 회사별 이력 캐시: { [companyCode]: { companyName, products, history } }
  const [historyMap,    setHistoryMap]    = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving,         setSaving]        = useState(false);

  // ── 이력 조회 ───────────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async (companyCode) => {
    setHistoryLoading(true);
    const r = await apiClient.get(`/api/admin/settings/settlement/history?companyCode=${companyCode}`);
    setHistoryLoading(false);
    if (!r.ok) { showToast('이력을 불러오지 못했습니다.', 'error'); return; }
    setHistoryMap((prev) => ({
      ...prev,
      [companyCode]: {
        companyName: r.data.companyName,
        products:    r.data.products    || [],
        history:     r.data.history     || [],
      },
    }));
  }, [showToast]);

  // ── 특정 날짜 이력 저장 (신규 추가 또는 기존 날짜 수정) ─────────────────────
  // fields: { consultant_company_share, consultant_marketer_share, delivery_fee,
  //           prices: [{id, price}], sellingPrices: [{id, price}] }
  async function saveHistoryEntry(companyCode, effectiveFrom, fields) {
    setSaving(true);
    const r = await apiClient.put('/api/admin/settings/settlement', {
      companyCode,
      effectiveFrom,
      prices:       fields.prices       || [],
      sellingPrices: fields.sellingPrices || [],
      deliveryFee:  fields.delivery_fee  != null ? Number(fields.delivery_fee)  : undefined,
      consultantCompanyShare:  fields.consultant_company_share  != null ? Number(fields.consultant_company_share)  : undefined,
      consultantMarketerShare: fields.consultant_marketer_share != null ? Number(fields.consultant_marketer_share) : undefined,
      senderName:              fields.sender_name || undefined,
      rates:                   Array.isArray(fields.rates) ? fields.rates : undefined,
    });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return false; }
    showToast('저장됐습니다.', 'success');
    await fetchHistory(companyCode);
    return true;
  }

  // ── 특정 날짜 이력 삭제 ──────────────────────────────────────────────────────
  async function deleteHistoryEntry(companyCode, effectiveFrom) {
    setSaving(true);
    const r = await apiClient.delete(
      `/api/admin/settings/settlement/history/${effectiveFrom}?companyCode=${companyCode}`
    );
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '삭제 실패', 'error'); return false; }
    showToast('삭제됐습니다.', 'success');
    await fetchHistory(companyCode);
    return true;
  }

  return {
    historyMap,
    historyLoading,
    saving,
    fetchHistory,
    saveHistoryEntry,
    deleteHistoryEntry,
  };
}
